import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { To } from 'react-router-dom';
import { Typography } from '~/components/Typography';
import { CardActionsTitleText } from '~/components/cards';
import { LayoutSkeleton } from '~/components/layout-skeleton';
import type { ShowList__Show as Show } from '../../queries/queries.generated';
import {
  ShowCardChildren,
  ShowCardContainer,
  ShowCardIconContainer,
  ShowCardLink,
  ShowCardMetaContainer,
  ShowCardNotLink,
} from '../lib/styles';

type OptionalLinkProps = Pick<ShowCardProps, 'children' | 'to'>;

const OptionalLink = ({ children, to }: OptionalLinkProps) =>
  to ? (
    <ShowCardLink to={to}>{children}</ShowCardLink>
  ) : (
    <ShowCardNotLink>{children}</ShowCardNotLink>
  );

export interface ShowCardProps {
  children?: ReactNode;
  show: Show;
  to?: To;
}

export const ShowCard = ({ children, show, to }: ShowCardProps) => {
  const { t } = useTranslation();
  const viewCount = show.viewIds.length;

  return (
    <ShowCardContainer>
      <OptionalLink to={to}>
        <ShowCardIconContainer>
          <LayoutSkeleton
            layout={show.defaultView.layout}
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
          />
        </ShowCardIconContainer>
        <ShowCardMetaContainer>
          <CardActionsTitleText title={show.name} />
          <Typography color="text.secondary" variant="subtitle2">
            {viewCount} {t('common.pluralize.view_plural', { count: viewCount })}
          </Typography>
        </ShowCardMetaContainer>
      </OptionalLink>
      <ShowCardChildren>{children}</ShowCardChildren>
    </ShowCardContainer>
  );
};
